<script module>
  let idCounter = 0

  function nextTooltipId() {
    idCounter += 1
    return `tooltip-${idCounter}`
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte'

  interface Props {
    text: string | null | undefined
    children: Snippet
  }

  let { text, children }: Props = $props()

  const tooltipId = nextTooltipId()

  let visible = $state(false)
  let x = $state(0)
  let y = $state(0)
  let tooltipEl = $state<HTMLDivElement | null>(null)
  let anchorEl = $state<HTMLSpanElement | null>(null)

  function show(e: PointerEvent) {
    if (!text) return
    visible = true
    move(e)
  }

  function move(e: PointerEvent) {
    if (!visible || !text) return
    const vw = window.innerWidth
    const tw = tooltipEl?.offsetWidth ?? 240
    x = Math.min(e.clientX + 12, vw - tw - 8)
    y = e.clientY + 20
  }

  function hide() {
    visible = false
  }

  function onFocus() {
    if (!text || !anchorEl) return
    visible = true
    const rect = anchorEl.getBoundingClientRect()
    const vw = window.innerWidth
    const tw = tooltipEl?.offsetWidth ?? 240
    x = Math.min(rect.left, vw - tw - 8)
    y = rect.bottom + 6
  }
</script>

<!-- The mouse handlers are a pointer-only enhancement; keyboard users get the
     tooltip via onfocus/onblur on the focusable children, and aria-describedby
     exposes it to assistive tech. A presentational role can't carry ARIA props,
     so ignore the static-interaction warning rather than drop the description. -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<span
  bind:this={anchorEl}
  class="tooltip-anchor"
  onpointerenter={show}
  onpointermove={move}
  onpointerleave={hide}
  onfocus={onFocus}
  onblur={hide}
  aria-describedby={text ? tooltipId : undefined}
>
  {@render children()}
</span>

{#if text}
  <div
    bind:this={tooltipEl}
    id={tooltipId}
    class="tooltip"
    class:tooltip--visible={visible}
    style="left: {x}px; top: {y}px"
    role="tooltip"
    aria-hidden={!visible}
  >
    {text}
  </div>
{/if}

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .tooltip-anchor {
    display: contents;
  }

  .tooltip {
    position: fixed;
    z-index: 1000;
    max-width: 320px;
    padding: v.$space-sm v.$space-md;
    background: v.$color-sidebar-bg;
    color: v.$color-sidebar-fg-strong;
    border: 1px solid v.$color-sidebar-divider;
    border-radius: v.$radius-md;
    box-shadow: v.$shadow-lg;
    font-size: 0.875rem;
    font-family: v.$font-sans;
    line-height: 1.5;
    pointer-events: none;
    white-space: normal;
    word-break: break-word;
    border-left: 3px solid v.$color-primary;
    visibility: hidden;

    &--visible {
      visibility: visible;
    }
  }
</style>
